import { lazy, useCallback, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useDatos } from './store/datos'
import { useSesion } from './store/sesion'
import { aplicarTema, escucharTemaSistema, useUI } from './store/ui'
import { Layout } from './components/Layout'
import { Isotipo } from './components/Logo'
import { RecuperarActualizacion } from './components/RecuperarActualizacion'
import { AvisoActualizacion } from './components/AvisoActualizacion'
import { Entrar } from './pages/Entrar'
import { Inicio } from './pages/Inicio'
import { Inventario } from './pages/Inventario'
import { Vender } from './pages/Vender'
import { Ventas } from './pages/Ventas'
import { Clientes } from './pages/Clientes'

/** Las cinco pantallas de la barra inferior van en el paquete inicial:
 *  son las que el vendedor abre apenas entra. El resto se descarga la
 *  primera vez que se visita, para que el arranque en terreno sea corto
 *  aunque la señal sea mala. */
// Bitácora se carga aparte aunque sea una pantalla de uso diario: es la
// que tiene el mapa, y venía arrastrando la librería de mapas al
// arranque. Ver el comentario en la propia pantalla.
const BitacoraVentas = lazy(() =>
  import('./pages/BitacoraVentas').then((m) => ({ default: m.BitacoraVentas })),
)
const ProductoDetalle = lazy(() => import('./pages/Producto').then((m) => ({ default: m.ProductoDetalle })))
const Ingreso = lazy(() => import('./pages/Ingreso').then((m) => ({ default: m.Ingreso })))
const VentaDetalle = lazy(() => import('./pages/Venta').then((m) => ({ default: m.VentaDetalle })))
const ClienteDetalle = lazy(() => import('./pages/Cliente').then((m) => ({ default: m.ClienteDetalle })))
const Pedidos = lazy(() => import('./pages/Pedidos').then((m) => ({ default: m.Pedidos })))
const Cobranza = lazy(() => import('./pages/Cobranza').then((m) => ({ default: m.Cobranza })))
const Devoluciones = lazy(() => import('./pages/Devoluciones').then((m) => ({ default: m.Devoluciones })))
const Reportes = lazy(() => import('./pages/Reportes').then((m) => ({ default: m.Reportes })))
const Estadisticas = lazy(() => import('./pages/Estadisticas').then((m) => ({ default: m.Estadisticas })))
const Gastos = lazy(() => import('./pages/Gastos').then((m) => ({ default: m.Gastos })))
const Pagos = lazy(() => import('./pages/Pagos').then((m) => ({ default: m.Pagos })))
const Despachos = lazy(() => import('./pages/Despachos').then((m) => ({ default: m.Despachos })))
const Usuarios = lazy(() => import('./pages/Usuarios').then((m) => ({ default: m.Usuarios })))
const PerfilVendedor = lazy(() =>
  import('./pages/PerfilVendedor').then((m) => ({ default: m.PerfilVendedor })),
)
const Bitacora = lazy(() => import('./pages/Bitacora').then((m) => ({ default: m.Bitacora })))
const Ajustes = lazy(() => import('./pages/Ajustes').then((m) => ({ default: m.Ajustes })))
const Sucursales = lazy(() => import('./pages/Sucursales').then((m) => ({ default: m.Sucursales })))
const Suscriptores = lazy(() =>
  import('./pages/Suscriptores').then((m) => ({ default: m.Suscriptores })),
)
const SucursalDetalle = lazy(() =>
  import('./pages/Sucursal').then((m) => ({ default: m.SucursalDetalle })),
)

/** Bloquea las rutas cuando no hay sesión, las de administración cuando
 *  quien mira es un vendedor, Vender cuando quien mira no vende, y
 *  Despachos cuando quien mira no reparte. */
function Protegida({
  soloAdmin = false,
  soloDespacho = false,
  soloVendedor = false,
}: {
  soloAdmin?: boolean
  soloDespacho?: boolean
  soloVendedor?: boolean
}) {
  const usuario = useSesion((s) => s.usuario)
  const ubicacion = useLocation()

  if (!usuario) return <Navigate to="/entrar" state={{ desde: ubicacion.pathname }} replace />
  if (soloAdmin && usuario.rol !== 'admin') return <Navigate to="/" replace />
  if (soloDespacho && usuario.rol !== 'admin' && usuario.rol !== 'despacho') {
    return <Navigate to="/" replace />
  }
  // NovaBerry (administración) y Despachos son cuentas de sistema, no
  // vendedores: no registran ventas, aunque entren directo por la URL.
  if (soloVendedor && usuario.rol !== 'vendedor') return <Navigate to="/" replace />
  // "Despachos" es una pantalla sola, sin el resto de la app alrededor:
  // a quien entra así se lo manda derecho ahí, y no puede salirse a
  // navegar el resto (no le corresponde ver ventas, inventario, etc).
  if (usuario.rol === 'despacho' && ubicacion.pathname !== '/despachos') {
    return <Navigate to="/despachos" replace />
  }
  return <Outlet />
}

/** "Perfil" en la barra principal siempre apunta a uno mismo — no hay
 *  una URL fija para eso, así que se resuelve acá y se reenvía al
 *  perfil real de quien esté conectado en este momento. */
function MiPerfil() {
  const usuario = useSesion((s) => s.usuario)!
  return <Navigate to={`/vendedores/${usuario.id}`} replace />
}

/** Cada cambio de ruta parte arriba: en móvil, mantener el scroll
 *  anterior desorienta. */
function AlSubir() {
  const { pathname } = useLocation()
  // El cuerpo va entre llaves a propósito: con la forma corta
  // (`() => window.scrollTo(0, 0)`) el efecto devuelve lo que devuelva
  // scrollTo, y React toma ese valor como la función de limpieza. Si
  // algo en el navegador —una extensión, un polyfill de scroll suave—
  // hace que scrollTo devuelva algo distinto de undefined, React
  // intenta llamarlo al desmontar y revienta con "no es una función",
  // tumbando la app entera.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

/** Cuánto dura la animación de arranque: 114 cuadros a 40 ms.
 *
 *  Van a 25 por segundo, los mismos del video original. Se probó
 *  bajarlos a 20 para que el archivo pesara menos y se notaba: la
 *  animación se veía frenada. No vale la pena ahorrar 100 kB —que
 *  además se descargan una sola vez— a cambio de que lo primero que ve
 *  la persona al abrir la app se vea a tirones. */
const DURACION_ARRANQUE = 4560
/** Tope duro por si la animación nunca llega a cargar. */
const TOPE_ARRANQUE = 7000

function Arranque({ onVideoListo }: { onVideoListo: () => void }) {
  // Esto era un <video> y en iPhone aparecía el botón de play: había que
  // tocarlo para que arrancara. No le faltaba nada —estaba mudo por
  // atributo Y por propiedad, con playsInline, y además se llamaba
  // play() a mano—: con el Modo de bajo consumo encendido, iOS bloquea
  // el autoplay de TODO video, incluso uno mudo, y no hay atributo que
  // lo evite.
  //
  // Una imagen animada no pasa por esa política: se anima sola siempre,
  // en cualquier teléfono y con la batería como esté. Como esta
  // animación nunca tuvo pista de audio, cambiarla por un WebP animado
  // no pierde absolutamente nada.
  //
  // El .mp4 queda como respaldo solo para navegadores sin WebP animado
  // (Safari 13 y anteriores); ahí vuelve a aparecer el botón de play,
  // pero es preferible a un cuadro roto.
  const [sinWebp, setSinWebp] = useState(false)
  const [cargada, setCargada] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // El contador arranca recién cuando la animación cargó. Si arrancara
  // al montar, con conexión lenta la animación se cortaría a la mitad.
  useEffect(() => {
    if (!cargada) return
    const fin = setTimeout(onVideoListo, DURACION_ARRANQUE)
    return () => clearTimeout(fin)
  }, [cargada, onVideoListo])

  // Red de seguridad: pase lo que pase, nadie se queda pegado acá.
  useEffect(() => {
    const tope = setTimeout(onVideoListo, TOPE_ARRANQUE)
    return () => clearTimeout(tope)
  }, [onVideoListo])

  useEffect(() => {
    if (!sinWebp) return
    const video = videoRef.current
    if (!video) return
    video.muted = true
    video.play().catch(() => {})
  }, [sinWebp])

  return (
    <div className="flex min-h-dvh flex-col superficie">
      <div className="grid flex-1 place-items-center">
        {/* El fondo de la animación ya es del mismo color que el fondo de
            la app ("superficie"), así que no necesita marco, círculo ni
            sombra: se ve todo de una pieza. */}
        {sinWebp ? (
          <video
            ref={videoRef}
            className="size-56 object-contain sm:size-64"
            src="/arranque.mp4"
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={onVideoListo}
            onError={onVideoListo}
          />
        ) : (
          <img
            className="size-56 object-contain sm:size-64"
            src="/arranque.webp"
            alt=""
            // "fetchPriority" y no "decoding=sync": las dos suenan a
            // «apúrate», pero hacen cosas opuestas. La primera pide que
            // el archivo se descargue antes que el resto, que es lo que
            // queremos. La segunda obliga a decodificar en el hilo
            // principal, y con una animación de 114 cuadros eso lo
            // bloquea justo cuando arranca —se ve a tirones—.
            fetchPriority="high"
            onLoad={() => setCargada(true)}
            onError={() => setSinWebp(true)}
          />
        )}
      </div>

      {/* Crédito del desarrollador, chico y discreto —no compite con la
          animación, solo queda ahí abajo para quien se fije. */}
      <div className="safe-bottom flex flex-col items-center gap-2 pb-10">
        <p className="text-[13px] font-medium texto-tenue">Una aplicación de:</p>
        <a
          href="https://www.digitalarix.cl"
          // En pestaña aparte y no en la misma: instalada como app, esta
          // pantalla no tiene barra de direcciones ni botón de atrás, así
          // que abrir el sitio encima dejaría a la persona sin forma de
          // volver a NovaBerry. "noopener" además evita que el sitio que
          // se abre pueda manipular esta ventana.
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Ir al sitio de Digital aRix"
          // "inline-flex" no es decorativo: un <a> es inline por defecto
          // y la imagen de adentro es block (lo pone el reset de
          // Tailwind). Un bloque dentro de un inline deja al enlace con
          // una caja de 16 px —solo su relleno—: el logo se ve entero,
          // pero el área que responde al toque es una franja mínima.
          //
          // El relleno además da margen al dedo: el logo tiene 48 px de
          // alto y con p-2 el área de toque queda en 64.
          className="inline-flex rounded-xl p-2 transition-opacity active:opacity-60"
        >
          <img
            src="/logo-digitalarix.png"
            alt="Digital aRix"
            className="h-11 w-auto object-contain opacity-90 sm:h-12"
          />
        </a>
      </div>
    </div>
  )
}

export default function App() {
  const inicializar = useDatos((s) => s.inicializar)
  const listo = useDatos((s) => s.listo)
  const error = useDatos((s) => s.error)
  const tema = useUI((s) => s.tema)
  // La pantalla de carga no se cierra apenas los datos están listos: se
  // queda hasta que el video también termine, para que dure lo mismo
  // que el video en vez de cortarlo a la mitad.
  const [videoListo, setVideoListo] = useState(false)
  // Con `useCallback` y no una flecha suelta: si cambiara de identidad
  // en cada render, el efecto que cuenta la duración de la animación se
  // reiniciaría cada vez que el store avisa algo, y la pantalla de carga
  // duraría de más sin ninguna razón visible.
  const marcarVideoListo = useCallback(() => setVideoListo(true), [])

  useEffect(() => {
    aplicarTema(tema)
    return escucharTemaSistema()
  }, [tema])

  useEffect(() => {
    void inicializar()
  }, [inicializar])

  if (!listo || !videoListo) {
    if (error) {
      return (
        <div className="grid min-h-dvh place-items-center superficie p-6">
          <div className="max-w-sm text-center">
            <Isotipo className="mx-auto mb-5 size-14" />
            <h1 className="text-xl font-bold texto-base">No se pudieron cargar los datos</h1>
            <p className="mt-2 text-sm texto-suave">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 h-12 rounded-2xl bg-bosque-500 px-6 font-semibold text-white transition-colors hover:bg-bosque-600"
            >
              Reintentar
            </button>
          </div>
        </div>
      )
    }
    return <Arranque onVideoListo={marcarVideoListo} />
  }

  return (
    <RecuperarActualizacion>
      <AvisoActualizacion />
      <BrowserRouter>
        <AlSubir />
        <Routes>
          <Route path="/entrar" element={<Entrar />} />

          <Route element={<Protegida />}>
            <Route element={<Layout />}>
              <Route index element={<Inicio />} />
              <Route path="inventario" element={<Inventario />} />
              <Route path="inventario/:id" element={<ProductoDetalle />} />

              <Route element={<Protegida soloVendedor />}>
                {/* NovaBerry y Despachos no venden: esta pantalla es
                    solo para cuentas de vendedor. */}
                <Route path="vender" element={<Vender />} />
              </Route>

              {/* El mapa de a quién visitar va FUERA de esa guarda a
                  propósito: es trabajo de terreno, pero el
                  administrador —que es quien armó la lista de locales—
                  también tiene que poder mirarlo y ver si dos
                  vendedores se están pisando. */}
              <Route path="vender/bitacora" element={<BitacoraVentas />} />

              <Route path="ventas" element={<Ventas />} />
              <Route path="ventas/:id" element={<VentaDetalle />} />
              <Route path="clientes" element={<Clientes />} />
              <Route path="clientes/:id" element={<ClienteDetalle />} />
              <Route path="pedidos" element={<Pedidos />} />
              <Route path="cobranza" element={<Cobranza />} />
              <Route path="devoluciones" element={<Devoluciones />} />
              <Route path="ajustes" element={<Ajustes />} />
              <Route path="perfil" element={<MiPerfil />} />
              {/* No es "solo admin": un vendedor puede ver su propio
                  perfil. La página misma decide si puede ver el de
                  alguien más. */}
              <Route path="vendedores/:id" element={<PerfilVendedor />} />

              <Route element={<Protegida soloDespacho />}>
                <Route path="despachos" element={<Despachos />} />
              </Route>

              <Route element={<Protegida soloAdmin />}>
                {/* El costo se entra siempre acá: el vendedor no debe
                    poder abrir esta pantalla ni por URL directa. */}
                <Route path="ingreso" element={<Ingreso />} />
                <Route path="reportes" element={<Reportes />} />
                <Route path="estadisticas" element={<Estadisticas />} />
                <Route path="gastos" element={<Gastos />} />
                <Route path="pagos" element={<Pagos />} />
                <Route path="usuarios" element={<Usuarios />} />
                <Route path="bitacora" element={<Bitacora />} />
                <Route path="sucursales" element={<Sucursales />} />
                <Route path="sucursales/:id" element={<SucursalDetalle />} />
                <Route path="suscriptores" element={<Suscriptores />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </RecuperarActualizacion>
  )
}
